import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import styled from 'styled-components';

import { VerticalDivider } from 'components/lib/VerticalDivider';
import { SalesPlaybookType } from 'types/recommendations';

import {
  SALES_PLAYBOOK_SECTION_ID,
  SALES_PLAYBOOK_STATUS,
  SALES_PLAYBOOK_VARIANT_CLASS,
  SALES_TALK_TRACKS_LABEL,
} from './constants';
import SalesPlaybookSection from './SalesPlaybookSection';
import { scrollToSalesTalkTrackHashTarget, scrollToSection } from './utils';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  max-width: 1170px;
  margin: 10px auto;
  padding: 10px 30px 0 30px;
`;

const SummaryContainer = styled.div`
  border-radius: 10px;
  border: solid 1px var(--line-gray);
  padding: 30px;
`;

const SummaryHeader = styled.div`
  display: flex;
  flex-flow: row;
  gap: 10px;
`;

const ImageContainer = styled.div`
  background-image: linear-gradient(315deg, #5f5fff, #f05fff 50%, #ff5b98 0%);
  width: 32px;
  height: 32px;
  min-width: 32px;
  padding: 6px;
  border-radius: 2px;
  line-height: 0;
  align-self: flex-start;
  box-sizing: border-box;

  img {
    display: block;
    width: 20px;
    height: 20px;
  }
`;

const SummaryTitle = styled.div`
  font-size: 20px;
  font-family: var(--font-bold);
`;

const SummarySubtitle = styled.div`
  font-size: 12px;
  color: var(--text-secondary);
`;

const SummarySubtitleParagraph = styled.p`
  margin: 0;

  & + & {
    margin-top: 4px;
  }
`;

const CountsRow = styled.div`
  display: flex;
  flex-flow: row;
  gap: 20px;
  margin-top: 24px;
`;

const CountItem = styled.a`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  cursor: pointer;
  text-decoration: none;
  color: inherit;
`;

const CountHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const CountLabel = styled.span`
  font-size: 28px;
`;

const CountNumberPill = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 38px;
  box-sizing: border-box;
  padding: 6px 10px 0;
  border-radius: 4px;
  font-size: 28px;
  line-height: 1;

  &.lead-with {
    background-image: linear-gradient(to bottom, var(--mint-pale), var(--mint-soft));
    color: var(--positive-green);
  }

  &.position-against {
    background-image: linear-gradient(to bottom, var(--lavender-pale), var(--lavender-soft));
    color: var(--competitor-purple);
  }

  &.handle-objections {
    background-image: linear-gradient(to bottom, var(--gold-pale), var(--gold-soft));
    color: var(--inactive-orange);
  }
`;

const CountDescription = styled.div`
  font-size: 12px;
  color: var(--text-secondary);
  line-height: 1.4;
`;

const Footer = styled.div`
  font-size: 12px;
  font-family: var(--font-regular);
  color: var(--text-secondary);
  padding: 10px 0 20px 0;
  display: flex;
  align-items: center;
  gap: 6px;
`;

type Props = {
  data: SalesPlaybookType;
};

const SalesPlaybookContent = ({ data }: Props) => {
  const { hash } = useLocation();
  const leadWith = data.categories.filter((cat) => cat.status === SALES_PLAYBOOK_STATUS.LEAD_WITH);
  const positionAgainst = data.categories.filter((cat) => cat.status === SALES_PLAYBOOK_STATUS.POSITION_AGAINST);
  const handleObjections = data.categories.filter((cat) => cat.status === SALES_PLAYBOOK_STATUS.HANDLE_OBJECTIONS);

  useEffect(() => {
    if (!hash) {
      return;
    }

    const animationFrame = requestAnimationFrame(() => {
      scrollToSalesTalkTrackHashTarget(hash, data.categories);
    });

    return () => cancelAnimationFrame(animationFrame);
  }, [data.categories, hash]);

  return (
    <Container>
      <SummaryContainer>
        <SummaryHeader>
          <ImageContainer>
            <img src="/icons/chrome-reader-mode.svg" width="20" height="20" alt="" />
          </ImageContainer>
          <div>
            <SummaryTitle>{SALES_TALK_TRACKS_LABEL}</SummaryTitle>
            <SummarySubtitle>
              <SummarySubtitleParagraph>
                Personalized conversation guides for every family need - powered by your competitive intelligence data.
              </SummarySubtitleParagraph>
              <SummarySubtitleParagraph>
                Each section below maps a core family priority to your specific strengths and positioning vs.
                competitors.
              </SummarySubtitleParagraph>
            </SummarySubtitle>
          </div>
        </SummaryHeader>
        <CountsRow>
          <CountItem
            href={`#${SALES_PLAYBOOK_SECTION_ID.LEAD_WITH}`}
            onClick={(e) => {
              e.preventDefault();
              scrollToSection(SALES_PLAYBOOK_SECTION_ID.LEAD_WITH);
            }}
          >
            <CountHeader>
              <CountLabel>Lead With</CountLabel>
              <CountNumberPill className={SALES_PLAYBOOK_VARIANT_CLASS[SALES_PLAYBOOK_STATUS.LEAD_WITH]}>
                {leadWith.length}
              </CountNumberPill>
              <img src="/icons/chevron-right.svg" width="24" height="24" alt="" />
            </CountHeader>
            <CountDescription>
              These are the areas where we clearly win and should highlight in conversations.
            </CountDescription>
          </CountItem>

          <VerticalDivider />

          <CountItem
            href={`#${SALES_PLAYBOOK_SECTION_ID.POSITION_AGAINST}`}
            onClick={(e) => {
              e.preventDefault();
              scrollToSection(SALES_PLAYBOOK_SECTION_ID.POSITION_AGAINST);
            }}
          >
            <CountHeader>
              <CountLabel>Position Against</CountLabel>
              <CountNumberPill className={SALES_PLAYBOOK_VARIANT_CLASS[SALES_PLAYBOOK_STATUS.POSITION_AGAINST]}>
                {positionAgainst.length}
              </CountNumberPill>
              <img src="/icons/chevron-right.svg" width="24" height="24" alt="" />
            </CountHeader>
            <CountDescription>
              These are areas where we&apos;re competitive, and how we position matters.
            </CountDescription>
          </CountItem>

          <VerticalDivider />

          <CountItem
            href={`#${SALES_PLAYBOOK_SECTION_ID.HANDLE_OBJECTIONS}`}
            onClick={(e) => {
              e.preventDefault();
              scrollToSection(SALES_PLAYBOOK_SECTION_ID.HANDLE_OBJECTIONS);
            }}
          >
            <CountHeader>
              <CountLabel>Handle Objections</CountLabel>
              <CountNumberPill className={SALES_PLAYBOOK_VARIANT_CLASS[SALES_PLAYBOOK_STATUS.HANDLE_OBJECTIONS]}>
                {handleObjections.length}
              </CountNumberPill>
              <img src="/icons/chevron-right.svg" width="24" height="24" alt="" />
            </CountHeader>
            <CountDescription>
              These are areas where prospects may push back, and we need to be prepared.
            </CountDescription>
          </CountItem>
        </CountsRow>
      </SummaryContainer>

      {leadWith.length > 0 && (
        <SalesPlaybookSection
          id={SALES_PLAYBOOK_SECTION_ID.LEAD_WITH}
          variantClass={SALES_PLAYBOOK_VARIANT_CLASS[SALES_PLAYBOOK_STATUS.LEAD_WITH]}
          iconSrc="/icons/sales-playbook-lead-with.svg"
          title={`${leadWith.length} Areas to Lead With`}
          categories={leadWith}
        />
      )}

      {positionAgainst.length > 0 && (
        <SalesPlaybookSection
          id={SALES_PLAYBOOK_SECTION_ID.POSITION_AGAINST}
          variantClass={SALES_PLAYBOOK_VARIANT_CLASS[SALES_PLAYBOOK_STATUS.POSITION_AGAINST]}
          iconSrc="/icons/sales-playbook-position-against.svg"
          title={`${positionAgainst.length} Areas to Position Against`}
          categories={positionAgainst}
        />
      )}

      {handleObjections.length > 0 && (
        <SalesPlaybookSection
          id={SALES_PLAYBOOK_SECTION_ID.HANDLE_OBJECTIONS}
          variantClass={SALES_PLAYBOOK_VARIANT_CLASS[SALES_PLAYBOOK_STATUS.HANDLE_OBJECTIONS]}
          iconSrc="/icons/sales-playbook-handle-objections.svg"
          title={`${handleObjections.length} Objections to Handle`}
          categories={handleObjections}
        />
      )}

      <Footer>
        <img src="/icons/error-outline-black.svg" width="16" height="16" alt="" />
        These talk tracks were generated by AI using your competitive intelligence data. Review and personalize scripts
        before use.
      </Footer>
    </Container>
  );
};

export default SalesPlaybookContent;
